// @flow
/* -----------------Types--------------- */
import type {
  LearningOrders,
  Score,
  UniqueAnswers,
  ProgressData,
  Distribution,
  PerformanceWithProgress,
  Image,
  Confidence,
  Content,
  SessionMeta,
  TimesChanged,
} from '../types';
import type { AnsweredFacts } from '../session/types';
import type {
  ItemVariants,
} from 'common/content/types';

const choiceEventType = {
  SELECT: 'SELECT',
  UNSELECT: 'UNSELECT',
  ANSWER: 'ANSWER',
  CROSSOUT: 'CROSSOUT',
  UNCROSSOUT: 'UNCROSSOUT',
  SKIP: 'SKIP',
  BACK: 'BACK',
  NEXT: 'NEXT',
  COMPLETE: 'COMPLETE',
  TERMINATE: 'TERMINATE',
  SHOW: 'SHOW',
};

type ChoiceEventType = $Keys<typeof choiceEventType>;

/* -------------------Types for event mutations----------------------- */

type MultiChoiceEventPayload = {
  questionId: string,
  selectionId: ?string,
  choices: ?string[],
  inReverseMode: ?boolean
}

type MultiChoiceEvent = {
  eventContext: *,
  eventType: ChoiceEventType,
  eventPayload: MultiChoiceEventPayload
};

type Choice = {
  index: string,
  text: string,
  images: Image[],
  termId?: ?string,
  numFirstSelections: number,
  explanation?: string,
  isCorrect?: boolean
};

type Tag = {
  id: string,
  label: string,
  type: string
}

type ReferencePosition = {
  chapter: string,
  section: string,
  subSection: string,
  pages: number[],
  refId: string,
  isTaskDone: boolean
}
type ReferenceSource = {
  title: string,
  thumbnail: string,
  author: string,
  link: string,
}
type Reference ={
  id: string,
  source: ReferenceSource,
  positions: ReferencePosition[],
  tags: Tag[]
}

type Question = {
  factId ?: ?string,
  termId?: ?string,
  id: string,
  questionText: string,
  explanation?: ?string,
  choices: Choice[],
  answerId: ?string,
  contentId: string,
  images: Image[],
  prompt?: string,
  previousScore: Score,
  currentScore: Score,
  correctAnswerText?: ?string,
  tags?: Tag[],
  references?: Reference[],
  variant?: ItemVariants,
  annotation?: {
    body: string,
    title: string
  },
    audio?: string,
    parentAudio?: string,
    parentStem?: string,
    index?: number,
    totalCount?: number,
    parentId?: string,
};

const answeredState = {
  correct: 'correct',
  incorrect: 'incorrect',
  none: 'none',
};

const sessionCompletionEventType = {
  complete: 'complete',
  terminate: 'terminate',
};

const selectionState = {
  unselected: 'unselected',
  selected: 'selected',
  crossedOut: 'crossedOut',
};

const reviewNavigationActionType = {
  forward: 'forward',
  backward: 'backward',
};

const checkBoxStatus = {
  checked: 'checked',
  unchecked: 'unchecked',
};

const answerStatusType = {
  correct: 1,
  incorrect: 0,
  answered: 2,
  skipped: -1,
};

type SelectionState = $Keys<typeof selectionState>;
type AnsweredStatus = $Keys<typeof answeredState>;
type ReviewNavigationActionType = $Keys<typeof reviewNavigationActionType>;
type AnswerStatusType = $Values<typeof answerStatusType>;
type CheckBoxStatus = $Keys<typeof checkBoxStatus>;
type SessionCompletionEventType = $Keys<typeof sessionCompletionEventType>;

type ChoiceStatus = {
  selectionStatus: SelectionState,
  answeredStatus: AnsweredStatus,
};

type ChoiceStatuses = {
  [string]: ChoiceStatus,
};
type Answer = {
  questionId: string,
  selectedChoiceIds: string[],
  correctChoiceId: ?string,
  confidence: number,
  answerStatus: boolean,
  choiceStatuses: ChoiceStatuses,
  score: number,
  previousScore?: ?number,
  termId?: ?string,
  factId?: ? string,
  timeTaken: number,
  timeFactor: number,
  contentId: string
};
type ReviewCheckBox = {
  incorrect: CheckBoxStatus,
  correct: CheckBoxStatus,
}
type MultiChoiceState = {
  reviewCheckBoxStatus: ReviewCheckBox,
  currentQuestionAnswered: boolean,
  inReverseMode: ?boolean,
  currentIndex: number,
  lastSeenIndex: number,
  sessionId: ?string,
  contextId: ?string,
  scheduleId: ?string,
  endTime: ?string,
  isSessionComplete: boolean,
  selectedChoiceIds: string[],
  isLastQuestionVisible: boolean,
  startTime: ?string,
  answers: Answer[],
  choiceStatuses: ChoiceStatuses,
  uniqueAnswers: UniqueAnswers,
  events: MultiChoiceEvent[],
  progressData: ProgressData,
  reviewQuestions: Question[],
  questions: Question[],
  sessionCreated: boolean,
  sessionLoading: boolean,
  requestedQuestionsCount: number,
  learningOrder: LearningOrders,
  summary: ?PerformanceWithProgress,
  reviewedContents: Content[],
  hasMore: boolean,
  answeredFacts: AnsweredFacts,
  inReviewMode: boolean,
  sessionMeta: SessionMeta,
  addedTaskIds: string[],
  timesChanged: TimesChanged
};

type Variant = 'practice' | 'quiz';

export {
  answeredState,
  answerStatusType,
  checkBoxStatus,
  choiceEventType,
  reviewNavigationActionType,
  selectionState,
  sessionCompletionEventType,
};

export type {
  Answer,
  AnsweredStatus,
  AnswerStatusType,
  Confidence,
  CheckBoxStatus,
  Choice,
  ChoiceEventType,
  ChoiceStatus,
  ChoiceStatuses,
  Distribution,
  MultiChoiceEvent,
  MultiChoiceState,
  PerformanceWithProgress,
  ProgressData,
  Question,
  ReviewNavigationActionType,
  SelectionState,
  ReviewCheckBox,
  SessionCompletionEventType,
  Variant,
  Tag,
  Reference,
};
